import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  v2GetPortfolioQueueOptions,
  v2GetStartupOutreachOutcomesOptions,
  v2MarkRepliesSeenMutation,
} from '@workspace/api-backend/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { outreachQueueQueryKey } from '../api'

/** How long a reply has to stay on screen before it counts as read. */
export const SEEN_DWELL_MS = 10_000

/** How much of the row has to be visible for the dwell timer to run. */
const VISIBLE_RATIO = 0.6

export type ReplyTrack = 'investor' | 'portfolio'

/**
 * Marks replies read once the founder has actually looked at them.
 *
 * Opening the page is not reading: a founder who lands on Outcomes has seen the
 * top of the list, not the twelve replies below the fold, and marking those seen
 * loses them from Home and the sidebar without anyone having read them. So a row
 * is only reported once it has been at least {@link VISIBLE_RATIO} visible for a
 * continuous {@link SEEN_DWELL_MS}.
 *
 * Ids are batched and flushed together — scrolling through a list fires many of
 * these in quick succession, and one request per row would be a burst of writes
 * for what is a single act of reading.
 */
export function useMarkRepliesSeen(startupId: string) {
  const mutation = useMutation(v2MarkRepliesSeenMutation())
  const invalidateHome = useInvalidateHomeState(startupId)
  const queryClient = useQueryClient()

  // Ids waiting to be sent, and the ones already sent this mount — a row
  // scrolled past twice must not be reported twice.
  const pending = useRef<{ investor: Set<string>; portfolio: Set<string> }>({
    investor: new Set(),
    portfolio: new Set(),
  })
  const reported = useRef(new Set<string>())
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(() => {
    const investorIds = [...pending.current.investor]
    const portfolioIds = [...pending.current.portfolio]
    if (investorIds.length === 0 && portfolioIds.length === 0) return
    pending.current.investor.clear()
    pending.current.portfolio.clear()
    mutation.mutate(
      { path: { id: startupId }, body: { investorIds, portfolioIds } },
      {
        // Everything that renders unread state has to be told: Home's cards, the
        // sidebar badge (which reads Home), the outcomes payload that draws the
        // reply rows, and the two campaign queues that draw the same dots on
        // their own boards. Without them the dots sit there after the badge has
        // already cleared, which reads as a bug. A failure is harmless — the
        // rows stay unread and the next visit reports them again.
        onSuccess: () => {
          invalidateHome()
          queryClient.invalidateQueries({
            queryKey: v2GetStartupOutreachOutcomesOptions({ path: { id: startupId } }).queryKey,
          })
          queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
          queryClient.invalidateQueries({
            queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
          })
        },
      }
    )
  }, [mutation, startupId, invalidateHome, queryClient])

  // `flush` closes over the mutation object, which React Query returns fresh on
  // every render. Reading it through a ref keeps the ref callbacks below stable
  // without them going stale.
  const flushRef = useRef(flush)
  flushRef.current = flush

  useEffect(() => {
    const timer = flushTimer
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  // One ref callback per row, created once and reused. Identity has to be stable
  // across renders: React detaches and re-attaches a ref whose identity changed,
  // which would tear down the observer and restart the dwell from zero on every
  // render — a founder reading a row would never accumulate the full dwell.
  const rowCallbacks = useRef(new Map<string, (node: HTMLElement | null) => void>())

  /**
   * Ref callback for a reply row. Attach to the element that represents one
   * reply; it observes visibility and reports the id after the dwell.
   */
  const observeRow = useCallback((id: string | null | undefined, track: ReplyTrack) => {
    if (!id) return undefined
    const key = `${track}:${id}`
    const cached = rowCallbacks.current.get(key)
    if (cached) return cached

    const callback = (node: HTMLElement | null) => {
      if (!node || reported.current.has(id)) return

      let dwell: ReturnType<typeof setTimeout> | null = null
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((e) => e.isIntersecting)
          if (visible && !dwell) {
            dwell = setTimeout(() => {
              if (reported.current.has(id)) return
              reported.current.add(id)
              pending.current[track].add(id)
              observer.disconnect()
              // Coalesce a scroll-through into one write.
              if (flushTimer.current) clearTimeout(flushTimer.current)
              flushTimer.current = setTimeout(() => flushRef.current(), 500)
            }, SEEN_DWELL_MS)
          } else if (!visible && dwell) {
            // Scrolled away before the dwell elapsed — it wasn't read.
            clearTimeout(dwell)
            dwell = null
          }
        },
        { threshold: VISIBLE_RATIO }
      )
      observer.observe(node)

      // React calls this when the row unmounts. Without it the observer outlives
      // the row it was watching, and a long session on the Outcomes page
      // accumulates one per row ever rendered.
      return () => {
        if (dwell) clearTimeout(dwell)
        observer.disconnect()
      }
    }

    rowCallbacks.current.set(key, callback)
    return callback
  }, [])

  return { observeRow }
}
